<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"
import route from "ziggy-js"
import { ChevronLeft, ChevronRight, ClipboardList, Pencil, Plus, Search, Trash2 } from "lucide-vue-next"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useToast } from "@/components/ui/toast/use-toast"
import AppLayout from "@/layouts/AppLayout.vue"
import AttendanceFormDialog from "./AttendanceFormDialog.vue"

defineOptions({
    layout: [AppLayout],
})

type Employee = {
    id: number
    name: string
    employee_number: string
    title: string
    is_active: boolean
}

type Attendance = {
    id: number
    employee_id: number
    work_date: string
    employee_number: string | null
    name: string | null
    title: string | null
    is_active: boolean
    time_in: string | null
    time_out: string | null
    timed_in_label: string | null
    timed_out_label: string | null
    hours: number | null
    status: "timed_in" | "timed_out" | "none"
}

type StoreHours = {
    default_start: string
    default_end: string
    opens_label: string
    closes_label: string
    opening_login_label: string
    staff_login_lead_minutes: number
}

type Props = {
    date: string
    dateLabel: string
    attendances: Attendance[]
    employees: Employee[]
    storeHours: StoreHours
}

const props = defineProps<Props>()
const page = usePage()
const { toast } = useToast()
const query = ref("")
const dialogOpen = ref(false)
const editing = ref<Attendance | null>(null)

watch(
    () => (page.props.flash as { status?: string } | undefined)?.status,
    (status) => {
        if (status) {
            toast({ description: status })
        }
    },
    { immediate: true }
)

const filteredAttendances = computed(() => {
    const search = query.value.trim().toLowerCase()

    if (!search) {
        return props.attendances
    }

    return props.attendances.filter((record) => {
        return [record.name, record.employee_number, record.title]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search))
    })
})

function shiftDate(days: number) {
    const next = new Date(`${props.date}T12:00:00`)
    next.setDate(next.getDate() + days)
    goToDate(next.toISOString().slice(0, 10))
}

function goToDate(date: string) {
    router.get(route("attendances.index"), { date }, { preserveState: true, preserveScroll: true })
}

function openCreate() {
    editing.value = null
    dialogOpen.value = true
}

function openEdit(record: Attendance) {
    editing.value = record
    dialogOpen.value = true
}

function destroyRecord(record: Attendance) {
    if (!confirm(`Delete attendance for ${record.name}?`)) {
        return
    }

    router.delete(route("attendances.destroy", record.id), {
        preserveScroll: true,
        onSuccess() {
            toast({ description: "Attendance deleted." })
        },
    })
}

function statusLabel(status: Attendance["status"]) {
    return {
        timed_out: "Timed out",
        timed_in: "Timed in",
        none: "Incomplete",
    }[status]
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8">
        <div class="bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h1 class="text-3xl font-bold tracking-tight sm:text-4xl">Attendance</h1>
                        <p class="mt-2 max-w-2xl text-white/80">{{ dateLabel }}</p>
                    </div>
                    <Button class="bg-white text-[rgb(181_115_5)] hover:bg-white/90" @click="openCreate">
                        <Plus class="mr-1 h-4 w-4" />
                        Add attendance
                    </Button>
                </div>
            </div>
        </div>

        <div class="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div class="relative max-w-md flex-1">
                    <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input v-model="query" class="pl-9" placeholder="Search by name or employee number" />
                </div>
                <div class="flex items-center gap-2">
                    <Button type="button" variant="outline" size="icon" @click="shiftDate(-1)">
                        <ChevronLeft class="h-4 w-4" />
                    </Button>
                    <Input
                        :model-value="date"
                        type="date"
                        class="w-[160px]"
                        @update:model-value="goToDate(String($event))"
                    />
                    <Button type="button" variant="outline" size="icon" @click="shiftDate(1)">
                        <ChevronRight class="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div class="overflow-hidden rounded-xl border bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Staff</TableHead>
                            <TableHead>Time in</TableHead>
                            <TableHead>Time out</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead class="w-[90px] text-right">
                                <span class="sr-only">Actions</span>
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        <TableRow v-for="record in filteredAttendances" :key="record.id">
                            <TableCell>
                                <p class="font-medium text-gray-900">{{ record.name }}</p>
                                <p class="text-xs text-muted-foreground">
                                    {{ record.employee_number }}
                                    <span v-if="record.title"> · {{ record.title }}</span>
                                </p>
                            </TableCell>
                            <TableCell class="text-sm">{{ record.timed_in_label ?? "—" }}</TableCell>
                            <TableCell class="text-sm">{{ record.timed_out_label ?? "—" }}</TableCell>
                            <TableCell class="text-sm">{{ record.hours ?? "—" }}</TableCell>
                            <TableCell>
                                <Badge :variant="record.status === 'timed_out' ? 'default' : 'secondary'">
                                    {{ statusLabel(record.status) }}
                                </Badge>
                            </TableCell>
                            <TableCell class="text-right">
                                <Button variant="ghost" size="icon" class="h-8 w-8" @click="openEdit(record)">
                                    <Pencil class="h-3.5 w-3.5" />
                                    <span class="sr-only">Edit {{ record.name }}</span>
                                </Button>
                                <Button variant="ghost" size="icon" class="h-8 w-8 text-destructive" @click="destroyRecord(record)">
                                    <Trash2 class="h-3.5 w-3.5" />
                                    <span class="sr-only">Delete {{ record.name }}</span>
                                </Button>
                            </TableCell>
                        </TableRow>
                        <TableRow v-if="filteredAttendances.length === 0">
                            <TableCell colspan="6" class="py-12 text-center text-sm text-muted-foreground">
                                <ClipboardList class="mx-auto mb-2 h-8 w-8 opacity-40" />
                                No attendance records for this date.
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>

        <AttendanceFormDialog
            v-model:open="dialogOpen"
            :date="date"
            :employees="employees"
            :store-hours="storeHours"
            :attendance="editing"
        />
    </div>
</template>
